import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Search, Menu, X, User, LogOut, Music, Home, Users, Upload, Compass, Shield } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { getImageUrl } from '../services/api';

function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/explore?search=${encodeURIComponent(searchQuery)}`);
      setSearchQuery('');
    }
  };

  const navLinks = [
    { to: '/', icon: Home, label: 'Home' },
    { to: '/explore', icon: Compass, label: 'Explore' },
    { to: '/artists', icon: Users, label: 'Crew Artists' },
    { to: '/upload', icon: Upload, label: 'Add Song' },
  ];

  const isExplorePage = location.pathname === '/explore';

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-md border-b border-neutral-800">
      <div className="px-2 md:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 md:gap-3 group shrink-0">
            <img src="/logo_small.webp" alt="Hariwood Logo" className="w-10 h-10 md:w-14 md:h-14 rounded-full shadow-lg shadow-yellow-500/20 group-hover:scale-110 transition-transform object-cover" />
            <div className="flex flex-col">
              <span className="text-sm md:text-2xl font-black tracking-tighter text-white leading-none">HARIWOOD</span>
              <span className="hidden md:block text-[9px] text-yellow-500 font-bold tracking-[0.1em] leading-none mt-1 uppercase">HARYANVI MUSIC</span>
            </div>
          </Link>

          {!isExplorePage && (
            <form onSubmit={handleSearch} className="flex-1 mx-2 md:mx-6 max-w-xl">
              <div className="relative w-full group">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-500" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-neutral-900 border border-neutral-800 rounded-full py-1.5 md:py-2 pl-9 pr-4 text-[10px] md:text-sm text-white focus:outline-none focus:border-yellow-500 transition-all shadow-inner"
                />
              </div>
            </form>
          )}

          {isExplorePage && <div className="flex-1" />}
 
          <div className="flex items-center gap-1 md:gap-4 shrink-0">
            <div className="hidden lg:flex items-center gap-4 mr-2">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="flex items-center gap-1.5 text-[11px] xl:text-[13px] font-semibold text-neutral-400 hover:text-white transition-colors"
                >
                  <link.icon className="w-4 h-4" />
                  {link.label}
                </Link>
              ))}
            </div>

            {user ? (
              <div className="flex items-center gap-2 md:gap-3">
                {user.role === 'Admin' && (
                  <Link
                    to="/admin"
                    className="hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-500/10 text-red-500 border border-red-500/20 hover:bg-red-500 hover:text-white transition-all text-xs font-bold"
                  >
                    <Shield className="w-3.5 h-3.5" />
                    Admin Panel
                  </Link>
                )}
                <Link
                  to="/dashboard"
                  className="flex w-8 h-8 md:w-9 md:h-9 shrink-0 rounded-full bg-yellow-500/10 items-center justify-center overflow-hidden border border-yellow-500/20 hover:border-yellow-500 transition-all shadow-lg"
                >
                  {user.avatar ? (
                    <img 
                      key={user.avatar}
                      src={getImageUrl(user.avatar)} 
                      alt={user.name} 
                      className="w-full h-full object-cover" 
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name || 'User')}&background=171717&color=EAB308`;
                      }}
                    />
                  ) : (
                    <User className="w-5 h-5 text-yellow-500" />
                  )}
                </Link>
                <button
                  onClick={() => {
                    logout();
                    navigate('/');
                  }}
                  className="p-2 text-neutral-400 hover:text-red-500 transition-colors"
                  title="Logout"
                >
                  <LogOut className="w-5 h-5" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2 md:gap-4">
                <Link to="/login" className="hidden sm:block text-xs md:text-sm font-semibold text-neutral-400 hover:text-white transition-colors">
                  Login
                </Link>
                <Link to="/signup" className="btn-primary text-[10px] md:text-sm whitespace-nowrap px-4 md:px-6 py-2 bg-yellow-500 text-black rounded-full font-bold hover:bg-yellow-400 transition-all shadow-lg shadow-yellow-500/20">
                  Sign Up
                </Link>
              </div>
            )}
            
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2 text-neutral-400 hover:text-white transition-colors"
              aria-label="Toggle navigation menu"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>

        {isMenuOpen && (
          <div className="md:hidden py-4 border-t border-neutral-800 px-2 leading-none">
            <form onSubmit={handleSearch} className="mb-4">
              <div className="relative group">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-neutral-900 border border-neutral-800 rounded-lg py-3 pl-10 pr-4 text-sm text-white focus:outline-none focus:border-yellow-500 shadow-inner mt-2"
                />
              </div>
            </form>
            <div className="flex flex-col gap-3">
              {navLinks.map(({ to, icon: Icon, label }) => (
                <Link
                  key={to}
                  to={to}
                  onClick={() => setIsMenuOpen(false)}
                  className="flex items-center gap-3 text-neutral-400 hover:text-yellow-500 transition-colors py-2"
                >
                  <Icon className="w-5 h-5" />
                  {label}
                </Link>
              ))}
              {user ? (
                <>
                  {user.role === 'Admin' && (
                    <Link
                      to="/admin"
                      onClick={() => setIsMenuOpen(false)}
                      className="flex items-center gap-3 text-red-400 font-bold py-2"
                    >
                      <Shield className="w-5 h-5" />
                      Admin Panel
                    </Link>
                  )}
                  <Link
                    to="/dashboard"
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center gap-3 text-neutral-400 hover:text-yellow-500 transition-colors py-2"
                  >
                    <User className="w-5 h-5" />
                    Profile
                  </Link>
                  <button
                    onClick={() => {
                      logout();
                      navigate('/');
                      setIsMenuOpen(false);
                    }}
                    className="flex items-center gap-3 text-red-500 py-2 w-full text-left"
                  >
                    <LogOut className="w-5 h-5" />
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    onClick={() => setIsMenuOpen(false)}
                    className="text-neutral-400 hover:text-yellow-500 transition-colors py-2"
                  >
                    Login
                  </Link>
                  <Link
                    to="/signup"
                    onClick={() => setIsMenuOpen(false)}
                    className="btn-primary text-center"
                  >
                    Sign Up
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}

export default Navbar;
